
import { supabase } from "@/integrations/supabase/client";
import { ProductDescription } from "@/lib/api";
import { requestCache } from "./requestCache";

/**
 * Synchronize a product description across all products in the same group
 * using a manual approach instead of RPC function
 */
export const manualSyncProductGroup = async (
  description: ProductDescription,
  editedText: string,
  originalText?: string
): Promise<{ success: boolean; count: number }> => {
  if (!description.content_id) {
    console.log("[MANUAL SYNC] No content_id, nothing to sync");
    return { success: true, count: 0 };
  }
  
  // Validate inputs
  if (!editedText) {
    console.error("[MANUAL SYNC] Missing editedText parameter, cannot sync");
    return { success: false, count: 0 };
  }
  
  // Log parameters for debugging
  console.log(`[MANUAL SYNC] Starting manual sync for group ${description.content_id} with parameters:`, {
    descriptionId: description.id,
    contentId: description.content_id,
    collectionId: description.collection_id,
    editedTextLength: editedText?.length,
    originalTextLength: originalText?.length,
    hasOriginalText: originalText !== undefined
  });
  
  try {
    // Create update data object with required fields
    const updateData: Record<string, any> = {
      improved_description: editedText,
      status: "pending", // Reset status when edited
      updated_at: new Date().toISOString()
    };
    
    // Add original_text if provided - this is critical for group consistency
    if (originalText !== undefined) {
      updateData.original_text = originalText;
      console.log("[MANUAL SYNC] Including original_text in update:", 
        originalText ? originalText.substring(0, 30) + '...' : 'empty string');
    } else {
      console.log("[MANUAL SYNC] No originalText provided, not updating original_text field");
    }
    
    // Get all products in this content group except the source product
    const { data: groupProducts, error: groupError } = await supabase
      .from("product_descriptions")
      .select("id")
      .eq("content_id", description.content_id)
      .eq("collection_id", description.collection_id)
      .neq("id", description.id);
      
    if (groupError) {
      console.error("[MANUAL SYNC] Error fetching group products:", groupError);
      throw groupError;
    }
    
    // If no other products in this group, nothing to sync
    if (!groupProducts || groupProducts.length === 0) {
      console.log("[MANUAL SYNC] No other products in the group to sync");
      return { success: true, count: 0 };
    }
    
    console.log(`[MANUAL SYNC] Found ${groupProducts.length} other products in the group to sync`);
    
    // Update each product one by one to avoid potential issues with bulk updates
    let syncedCount = 0;
    const productIds = groupProducts.map(product => product.id);
    
    // Log the actual data being sent to each update operation
    console.log("[MANUAL SYNC] Update data being sent:", {
      ...updateData,
      improved_description: updateData.improved_description?.substring(0, 30) + '...',
      original_text: updateData.original_text?.substring(0, 30) + '...'
    });
    
    // Use a transaction for better consistency
    try {
      // Update all products in one batch for better performance and consistency
      console.log("[MANUAL SYNC] Attempting batch update for product IDs:", productIds);
      
      const { error: batchUpdateError, data: batchUpdateData } = await supabase
        .from("product_descriptions")
        .update(updateData)
        .in("id", productIds)
        .select();
        
      if (batchUpdateError) {
        console.error(`[MANUAL SYNC] Error in batch update:`, batchUpdateError);
        throw batchUpdateError;
      }
      
      syncedCount = productIds.length;
      console.log(`[MANUAL SYNC] Successfully batch synced ${syncedCount} products. Response data:`, batchUpdateData);
    } catch (batchError) {
      console.error(`[MANUAL SYNC] Batch update failed, falling back to individual updates:`, batchError);
      
      // Batch update failed, try updating one by one
      for (const product of groupProducts) {
        try {
          console.log(`[MANUAL SYNC] Updating product ${product.id}`);
          
          const { error: updateError, data: updatedData } = await supabase
            .from("product_descriptions")
            .update(updateData)
            .eq("id", product.id)
            .select();
            
          if (updateError) {
            console.error(`[MANUAL SYNC] Error updating product ${product.id}:`, updateError);
            // Continue with other products even if one fails
          } else {
            console.log(`[MANUAL SYNC] Successfully updated product ${product.id}:`, updatedData);
            syncedCount++;
          }
        } catch (itemError) {
          console.error(`[MANUAL SYNC] Exception updating product ${product.id}:`, itemError);
        }
      }
    }
    
    console.log(`[MANUAL SYNC] Successfully synced ${syncedCount}/${groupProducts.length} products`);
    
    // Enhanced cache invalidation - Be aggressive with cache clearing
    console.log("[MANUAL SYNC] Clearing all caches to ensure fresh data");
    
    // Invalidate specific content group cache
    if (description.content_id) {
      requestCache.invalidateContentGroup(description.content_id);
      console.log(`[MANUAL SYNC] Invalidated cache for content group ${description.content_id}`);
    }
    
    // Invalidate collection-specific cache
    if (description.collection_id) {
      requestCache.invalidateCollection(description.collection_id);
      console.log(`[MANUAL SYNC] Invalidated cache for collection ${description.collection_id}`);
    }
    
    // Force specific query key invalidation
    requestCache.invalidateQueryKeys([
      ["productDescriptions", description.collection_id], 
      ["descriptions", description.collection_id],
      ["collectionStats"]
    ]);
    
    // Clear all general cache as final measure
    requestCache.clear();
    console.log("[MANUAL SYNC] Cleared all general cache");
    
    return { 
      success: syncedCount > 0,
      count: syncedCount
    };
  } catch (error) {
    console.error("[MANUAL SYNC] Error in manual sync process:", error);
    return { success: false, count: 0 };
  }
};
